<script lang="ts">
    /**
     * LandingNav - 홈페이지 고정 앱바
     *
     * 모든 페이지에서 동일한 메뉴(JadEye | Gridverse | Pin | Ink).
     * 앱 셸 구조: LandingApp이 한 번 마운트 후 고정, 컨텐츠만 클라 라우팅으로 전환.
     * JadEye 내부 섹션 점프(기능소개/가격)는 JadeyeSubNav가 담당.
     */
    import { getPin } from '../../../lib/pin';
    import { getInk } from '../../../lib/ink';

    interface Props {
        currentPath?: string;
    }

    let { currentPath = '/' }: Props = $props();

    // 📍 Pin — 꺼진 환경(dev)에서는 null 이라 메뉴에 나타나지 않는다.
    const pin = getPin();
    // 🖋️ Ink — 꺼진 환경에서는 null 이라 메뉴에 나타나지 않는다.
    const ink = getInk();

    let scrolled = $state(false);
    let mobileMenuOpen = $state(false);

    /*
     * 📢 서버가 그린 공지 배너(#site-notice)를 피해 앉는다.
     *
     * 🚨 이 앱바는 `position: fixed; top: 0` 이다 — 히어로 위에 투명하게 떠 있는 디자인이라
     *    자리를 차지하지 않는다. 그래서 `<body>` 맨 위에 흐름으로 놓인 공지 배너를 **그대로
     *    덮어** 글자가 겹쳤다(2026-09-09 prod, CEO 가 화면으로 잡았다).
     *    ⚠️ 렌더 여부를 grep 으로 "1건" 세는 검증으로는 절대 안 잡히는 종류다 —
     *       **있다**와 **보인다**는 다른 축이다.
     *
     * 🚫 높이를 상수로 박지 않는다. 문안이 화면 폭에 따라 1~3줄로 접혀 높이가 변한다
     *    (실제로 데스크톱 1줄 · 모바일 2줄이었다). 재서 쓰고, 크기가 바뀌면 다시 잰다.
     *
     * 🎯 스크롤하면 배너가 위로 사라지는 만큼 앱바도 따라 올라가 top:0 에 붙는다.
     *    (고정값으로 내려 두면 배너가 사라진 뒤 앱바 위에 빈 띠가 남는다)
     *
     * ℹ️ 배너가 없는 기간에는 높이가 0 이라 예전 동작과 정확히 같다.
     *    JS 가 없으면 이 앱바 자체가 렌더되지 않으므로(SPA) 겹칠 일도 없다.
     */
    let noticeHeight = $state(0);
    let scrollY = $state(0);
    const navTop = $derived(Math.max(0, noticeHeight - scrollY));

    $effect(() => {
        const el = document.getElementById('site-notice');
        if (! el) {
            noticeHeight = 0;
            return;
        }

        const measure = () => { noticeHeight = el.offsetHeight; };
        measure();

        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    });

    // 모든 페이지 공통 메뉴 (앱바 완전 고정) — 회사 차원 프로덕트만.
    // 가이드·커뮤니티(JadEye 전용)와 내부 섹션 점프는 JadeyeSubNav가 담당.
    //
    // ℹ️ Pin 은 **제품 소개 페이지(/pin)** 로 보낸다 — JadEye·Gridverse 와 같은 층이다(D9).
    //    콘텐츠(/pin/rest-area)로 직결하면 소개 없이 raw 목록으로 떨어져 다른 두 서비스와 형태가 어긋난다.
    //    앱바에 두는 이유: 랜딩 전 페이지(/ · /jadeye · /gridverse · /guide)에서 Pin 으로 가는
    //    링크가 한 번에 생긴다 — 홈에만 두면 나머지 3개 경로에서는 여전히 닿을 길이 없다.
    const navLinks = [
        { href: '/jadeye', label: 'JadEye' },
        { href: '/gridverse', label: 'Gridverse' },
        ...(pin ? [{ href: pin.landing, label: 'Pin' }] : []),
        ...(ink ? [{ href: ink.landing, label: 'Ink' }] : []),
    ];

    // 🎯 강조할 칸 — 가이드(/guide)는 JadEye 의 하위 섹션이라 JadEye 를 강조한다.
    //    서버 렌더 아티클(/guide/{slug})의 앱바가 JadEye 를 강조하므로, 여기서 꺼 두면 가이드 목록 → 글로
    //    넘어갈 때 같은 섹션인데 강조가 꺼졌다 켜진다(2026-09-11 CEO "앱바는 전체적으로 통일되게").
    const activeHref = $derived(currentPath === '/guide' ? '/jadeye' : currentPath);

    function handleScroll() {
        scrollY = window.scrollY;
        scrolled = window.scrollY > 20;
    }

    function toggleMobileMenu() {
        mobileMenuOpen = !mobileMenuOpen;
    }

    function closeMobileMenu() {
        mobileMenuOpen = false;
    }

    function handleNavClick(e: MouseEvent, href: string) {
        if (href.startsWith('#')) {
            e.preventDefault();
            closeMobileMenu();
            const el = document.querySelector(href);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
            }
        } else {
            closeMobileMenu();
        }
    }

    // TODO: 사전등록 활성화 시 scrollToPreRegister 복원
    // function scrollToPreRegister(e: MouseEvent) {
    //     e.preventDefault();
    //     closeMobileMenu();
    //     const el = document.querySelector('#pre-register');
    //     if (el) el.scrollIntoView({ behavior: 'smooth' });
    // }
</script>

<svelte:window onscroll={handleScroll} />

<nav class="landing-nav" class:scrolled style="top: {navTop}px">
    <div class="nav-inner">
        <!-- Logo -->
        <a href="/" class="nav-logo" onclick={closeMobileMenu}>
            <img src="/logo.svg" alt="JadEye Logo" class="nav-logo-img" />
            <span class="nav-logo-text">제이드아이</span>
        </a>

        <!-- Desktop Menu -->
        <div class="nav-center">
            {#each navLinks as link}
                <a
                    href={link.href}
                    class="nav-link"
                    class:active={!link.external && link.href === activeHref}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noopener noreferrer' : undefined}
                    aria-label={link.external ? `${link.label} (네이버 카페, 새 창에서 열림)` : undefined}
                    onclick={(e) => handleNavClick(e, link.href)}
                >
                    {link.label}
                    {#if link.external}
                        <svg class="nav-link-external" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M7 17 17 7" />
                            <path d="M7 7h10v10" />
                        </svg>
                    {/if}
                </a>
            {/each}
        </div>

        <!-- Mobile Hamburger -->
        <button class="nav-hamburger" onclick={toggleMobileMenu} aria-label="메뉴 열기">
            <span class="hamburger-line" class:open={mobileMenuOpen}></span>
            <span class="hamburger-line" class:open={mobileMenuOpen}></span>
            <span class="hamburger-line" class:open={mobileMenuOpen}></span>
        </button>
    </div>

    <!-- Mobile Menu -->
    {#if mobileMenuOpen}
        <div class="mobile-menu">
            {#each navLinks as link}
                <a
                    href={link.href}
                    class="mobile-link"
                    class:active={!link.external && link.href === activeHref}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noopener noreferrer' : undefined}
                    aria-label={link.external ? `${link.label} (네이버 카페, 새 창에서 열림)` : undefined}
                    onclick={(e) => handleNavClick(e, link.href)}
                >
                    {link.label}
                    {#if link.external}
                        <svg class="mobile-link-external" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M7 17 17 7" />
                            <path d="M7 7h10v10" />
                        </svg>
                    {/if}
                </a>
            {/each}
        </div>
    {/if}
</nav>

<style>
    .landing-nav {
        position: fixed;
        /* 🔗 실제 값은 인라인 style 이 준다(공지 배너 높이). 여기는 배너가 없을 때의 기본값. */
        top: 0;
        left: 0;
        right: 0;
        z-index: 100;
        padding: 1rem 1.5rem;
        transition: all 0.3s ease;
        background: transparent;
    }

    .landing-nav.scrolled {
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(16px);
        border-bottom: 1px solid rgba(30, 92, 58, 0.08);
        box-shadow: 0 1px 8px rgba(0, 0, 0, 0.04);
    }

    .nav-inner {
        max-width: 72rem;
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    /* Logo */
    .nav-logo {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        text-decoration: none;
        transition: opacity 0.2s;
    }

    .nav-logo:hover {
        opacity: 0.8;
    }

    .nav-logo-img {
        width: 2.5rem;
        height: 2.5rem;
        filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.08));
    }

    .nav-logo-text {
        font-size: 1.35rem;
        font-weight: 700;
        color: var(--landing-primary, #1E5C3A);
        letter-spacing: -0.02em;
    }

    /* Center Links */
    .nav-center {
        display: flex;
        align-items: center;
        gap: 0.25rem;
    }

    .nav-link {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        padding: 0.5rem 1rem;
        border-radius: 8px;
        text-decoration: none;
        font-size: 0.95rem;
        font-weight: 500;
        color: var(--landing-text-sub, #666666);
        transition: all 0.2s;
    }

    .nav-link-external {
        opacity: 0.5;
        transition: opacity 0.2s, transform 0.2s;
    }

    .nav-link:hover .nav-link-external {
        opacity: 1;
        transform: translate(1px, -1px);
    }

    .nav-link:hover {
        color: var(--landing-primary, #1E5C3A);
        background: rgba(30, 92, 58, 0.06);
    }

    .nav-link.active {
        color: var(--landing-primary, #1E5C3A);
        font-weight: 600;
        background: rgba(30, 92, 58, 0.08);
    }

    /* Right Store Badges */
    .nav-right {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .nav-badge-link {
        display: inline-flex;
        align-items: center;
        transition: opacity 0.2s, transform 0.2s;
    }

    .nav-badge-link:hover {
        opacity: 0.85;
        transform: translateY(-1px);
    }

    .nav-badge {
        height: 2.25rem;
        object-fit: contain;
        display: block;
    }

    /* 구글 플레이 PNG 내부 투명 여백 보정 */
    .nav-badge.gplay {
        height: 2.75rem;
        margin: -0.25rem 0;
    }

    /* Hamburger */
    .nav-hamburger {
        display: none;
        flex-direction: column;
        gap: 5px;
        background: none;
        border: none;
        cursor: pointer;
        padding: 0.5rem;
    }

    .hamburger-line {
        display: block;
        width: 22px;
        height: 2px;
        background: var(--landing-text, #1A1A1A);
        border-radius: 2px;
        transition: all 0.3s;
    }

    .hamburger-line.open:nth-child(1) {
        transform: translateY(7px) rotate(45deg);
    }

    .hamburger-line.open:nth-child(2) {
        opacity: 0;
    }

    .hamburger-line.open:nth-child(3) {
        transform: translateY(-7px) rotate(-45deg);
    }

    /* Mobile Menu */
    .mobile-menu {
        display: none;
        flex-direction: column;
        gap: 0.15rem;
        padding: 0.75rem;
        margin-top: 0.5rem;
        background: rgba(255, 255, 255, 0.98);
        backdrop-filter: blur(16px);
        border-radius: 12px;
        border: 1px solid rgba(30, 92, 58, 0.08);
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
        animation: slideDown 0.25s ease;
    }

    .mobile-link {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        padding: 0.7rem 1rem;
        border-radius: 8px;
        text-decoration: none;
        font-size: 0.95rem;
        font-weight: 500;
        color: var(--landing-text-sub, #666666);
        transition: all 0.2s;
    }

    .mobile-link-external {
        opacity: 0.5;
        transition: opacity 0.2s;
    }

    .mobile-link:hover .mobile-link-external {
        opacity: 1;
    }

    .mobile-link:hover {
        background: rgba(30, 92, 58, 0.06);
        color: var(--landing-primary, #1E5C3A);
    }

    .mobile-link.active {
        color: var(--landing-primary, #1E5C3A);
        font-weight: 600;
        background: rgba(30, 92, 58, 0.06);
    }

    .mobile-divider {
        height: 1px;
        background: rgba(30, 92, 58, 0.08);
        margin: 0.25rem 0.5rem;
    }

    .mobile-badges {
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
        padding: 0.5rem 0.5rem 0.25rem;
        align-items: stretch;
    }

    .mobile-badge-link {
        display: flex;
        justify-content: center;
        transition: opacity 0.2s;
    }

    .mobile-badge-link:hover {
        opacity: 0.85;
    }

    .mobile-badge {
        height: 2.75rem;
        object-fit: contain;
        display: block;
    }

    .mobile-badge.gplay {
        height: 3.2rem;
        margin: -0.3rem 0;
    }

    @keyframes slideDown {
        from { opacity: 0; transform: translateY(-8px); }
        to { opacity: 1; transform: translateY(0); }
    }

    @media (max-width: 768px) {
        .nav-center,
        .nav-right {
            display: none;
        }

        .nav-hamburger {
            display: flex;
        }

        .mobile-menu {
            display: flex;
        }
    }

    @media (min-width: 1024px) {
        .landing-nav {
            padding: 1.25rem 3rem;
        }

        .nav-logo-img {
            width: 2.75rem;
            height: 2.75rem;
        }

        .nav-logo-text {
            font-size: 1.5rem;
        }

        .nav-link {
            font-size: 1rem;
            padding: 0.5rem 1.2rem;
        }
    }
</style>
